import { useCallback, useId, useState, type ReactNode } from "react";
import { cssVars } from "../css-vars";
import { cn } from "../lib/cn";

/**
 * Flat chart fills from tokens.css (Tailwind resolves each class to `var(--color-…)`).
 * Class strings are literal so Tailwind can scan them. No gradients, ever.
 */
export const CHART_TONES = {
  brand: { fill: "fill-brand-500", stroke: "stroke-brand-500", swatch: "bg-brand-500" },
  "brand-light": { fill: "fill-brand-300", stroke: "stroke-brand-300", swatch: "bg-brand-300" },
  "brand-dark": { fill: "fill-brand-700", stroke: "stroke-brand-700", swatch: "bg-brand-700" },
  /** Navy series; lifts to ink-300 in dark mode so it stays visible on dark surfaces. */
  ink: { fill: "fill-ink-800 dark:fill-ink-300", stroke: "stroke-ink-800 dark:stroke-ink-300", swatch: "bg-ink-800 dark:bg-ink-300" },
  "ink-light": { fill: "fill-ink-300", stroke: "stroke-ink-300", swatch: "bg-ink-300" },
  signal: { fill: "fill-signal-500", stroke: "stroke-signal-500", swatch: "bg-signal-500" },
  success: { fill: "fill-success", stroke: "stroke-success", swatch: "bg-success" },
  danger: { fill: "fill-danger-fill", stroke: "stroke-danger-fill", swatch: "bg-danger-fill" },
} as const;

export type ChartTone = keyof typeof CHART_TONES;

/** Focus indicator for SVG marks (outline is unreliable on SVG; draw a stroke instead). */
export const MARK_FOCUS = "outline-none focus-visible:stroke-fg focus-visible:[stroke-width:2px]";

export const defaultFormat = (value: number): string => value.toLocaleString("en-PH");

export function niceMax(values: readonly number[]): number {
  const max = Math.max(0, ...values);
  if (max === 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(max));
  const step = magnitude / 2;
  return Math.ceil(max / step) * step;
}

export interface LegendItem {
  label: string;
  tone: ChartTone;
  /** Optional trailing text, e.g. "39%". */
  value?: string;
  /** `line` swatch for line charts. */
  shape?: "square" | "line";
}

/** Legend with text labels (never colour alone). */
export function ChartLegend({ items, className }: { items: readonly LegendItem[]; className?: string }) {
  return (
    <ul className={cn("flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs font-semibold text-fg-muted", className)}>
      {items.map((item) => (
        <li key={item.label} className="flex items-center gap-1.5">
          <span
            aria-hidden="true"
            className={cn(item.shape === "line" ? "h-0.5 w-4 rounded-pill" : "size-2.5 rounded-xs", CHART_TONES[item.tone].swatch)}
          />
          <span>{item.label}</span>
          {item.value ? <span className="tabular-nums text-fg">{item.value}</span> : null}
        </li>
      ))}
    </ul>
  );
}

export interface ChartDataTable {
  columns: readonly string[];
  rows: readonly (readonly (string | number)[])[];
}

/** Visually hidden data table fallback for screen-reader users. */
export function ChartTableFallback({ caption, table }: { caption: string; table: ChartDataTable }) {
  return (
    <table className="sr-only">
      <caption>{caption}</caption>
      <thead>
        <tr>
          {table.columns.map((column) => (
            <th key={column} scope="col">
              {column}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {table.rows.map((row, r) => (
          <tr key={r}>
            {row.map((cell, c) =>
              c === 0 ? (
                <th key={c} scope="row">
                  {cell}
                </th>
              ) : (
                <td key={c}>{cell}</td>
              ),
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export interface TooltipState {
  /** Percent of the plot box. */
  x: number;
  y: number;
  text: string;
  index: number;
}

export function useChartTooltip() {
  const [tip, setTip] = useState<TooltipState | null>(null);
  const show = useCallback((next: TooltipState) => {
    setTip(next);
  }, []);
  const hide = useCallback(() => {
    setTip(null);
  }, []);
  return { tip, show, hide };
}

/** Decorative tooltip (marks carry the same text as their accessible name). */
export function ChartTooltip({ tip }: { tip: TooltipState | null }) {
  if (!tip) return null;
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute left-[var(--geo-x)] top-[var(--geo-y)] z-10 -translate-x-1/2 -translate-y-full whitespace-nowrap rounded-md bg-ink-950 px-2.5 py-1.5 text-[11px] font-semibold text-white shadow-pop"
      style={cssVars({ "--geo-x": `${tip.x}%`, "--geo-y": `${tip.y}%` })}
    >
      {tip.text}
    </div>
  );
}

export interface SvgFrameProps {
  title: string;
  description?: string;
  viewBox: string;
  preserveAspectRatio?: string;
  className?: string;
  children: ReactNode;
  onMouseLeave?: () => void;
}

/** Labelled SVG root (`role=group` so focusable marks stay exposed) with <title>/<desc>. */
export function SvgFrame({ title, description, viewBox, preserveAspectRatio, className, children, onMouseLeave }: SvgFrameProps) {
  const id = useId();
  return (
    <svg
      role="group"
      aria-labelledby={`${id}-title`}
      aria-describedby={description ? `${id}-desc` : undefined}
      viewBox={viewBox}
      preserveAspectRatio={preserveAspectRatio}
      className={cn("block w-full overflow-visible", className)}
      onMouseLeave={onMouseLeave}
    >
      <title id={`${id}-title`}>{title}</title>
      {description ? <desc id={`${id}-desc`}>{description}</desc> : null}
      {children}
    </svg>
  );
}

/** Evenly spaced axis labels under a chart (as in the mockups). */
export function AxisLabels({ labels, className }: { labels: readonly string[]; className?: string }) {
  if (labels.length === 0) return null;
  return (
    <div aria-hidden="true" className={cn("mt-2 flex justify-between text-[11px] text-fg-subtle", className)}>
      {labels.map((label, i) => (
        <span key={`${label}-${i}`}>{label}</span>
      ))}
    </div>
  );
}
